// PLAY YOUR GAME™ — Configuración dinámica de mapas premium.
//
// Cada mapa se identifica por la combinación "dominant_secondary"
// (en minúsculas, sin acentos: leon, lobo, halcon, delfin).
//
// El template <MapaTemplate> renderiza EXACTAMENTE el mismo sistema visual
// (header, sidebar, 11 secciones cinematográficas, slider interactivo,
// paleta negro/blanco/rojo sombra, tipografía Inter) para CUALQUIER
// combinación. Solo cambia el contenido de este objeto.
//
// Para añadir un nuevo mapa: copia un bloque existente y reemplaza los
// textos. La UI se reusa sin tocar JSX.

export const ARCHETYPE_LABEL = {
  leon: "LEÓN",
  lobo: "LOBO",
  halcon: "HALCÓN",
  delfin: "DELFÍN",
};

/**
 * Strings globales de marca y navegación.
 * Son los mismos para los 16 mapas; viven aquí para que NADA quede hardcodeado
 * en MapaPage.jsx. Si en el futuro se internacionaliza o cambia el branding,
 * basta tocar este objeto.
 */
export const MAPA_GLOBAL = {
  brand: "PLAY YOUR GAME™",
  headerTagline: "CÓMO JUEGAS BAJO PRESIÓN",
  /**
   * FIXED hero line — identidad editorial PLAY YOUR GAME™.
   * Aparece SIEMPRE entre el título del avatar y la frase emocional única.
   * NO se puede sobrescribir desde el config de cada mapa.
   */
  heroFixedLine: "CÓMO JUEGAS BAJO PRESIÓN",
  cierreFooter: {
    copyright: "© 2026 PLAY YOUR GAME™. TODOS LOS DERECHOS RESERVADOS.",
    backToTop: "VOLVER AL INICIO",
    systemTagline: "SISTEMA DE EVOLUCIÓN BAJO PRESIÓN",
  },
  notFound: {
    eyebrow: "PLAY YOUR GAME™",
    body:
      "Este mapa todavía está en construcción. Vuelve pronto — estamos creando los 16 mapas con la misma profundidad que tu resultado merece.",
    back: "Volver al inicio",
  },
};

export const MAPAS = {
  delfin_halcon: {
    dominant: "DELFÍN",
    secondary: "HALCÓN",

    portada: {
      kicker: "PLAY YOUR GAME™",
      kickerRight: "DELFÍN dominante · HALCÓN secundario",
      quote:
        "Tu problema no es falta de empatía.\nEs sentir tanto lo que podría pasar…\nque empiezas a desaparecer del presente.",
      footerLeft: "La presión no cambia cómo juegas.\nRevela cómo juegas.",
      footerRight: "DELFÍN + HALCÓN",
    },

    tension1: {
      kicker: "LA PRESIÓN REVELA",
      frase:
        "Cuando todo se tensa…\nempiezas a cargar problemas que todavía no existen.",
    },

    arquetipo: {
      kicker: "PARTE I",
      titulo: "TUS DOS FUERZAS",
      rightLabel: "CÓMO REACCIONAS",
      dominante: {
        numero: "01",
        titulo: "EL DELFÍN: Empatía y Conexión",
        line:
          "Sientes a las personas antes de que digan algo.",
        body:
          "Tu sensibilidad lee el clima del grupo: dónde hay tensión, dónde hay miedo, dónde algo no encaja. No solo escuchas palabras, sientes lo que hay debajo. Por eso las personas confían en ti y se acercan cuando algo está por quebrarse. Tu fuerza es cuidar la armonía sin que se note.",
      },
      secundario: {
        numero: "02",
        titulo: "EL HALCÓN: Visión y Anticipación",
        line: "Le da rumbo a lo que sientes.",
        body:
          "Tu Halcón mira hacia adelante: el próximo conflicto, el impacto que tendrá, el riesgo que nadie todavía nombra. No solo sientes el presente, también ves cómo terminará. Por eso tu sensibilidad no se mueve sin rumbo: lee hoy y anticipa mañana.",
      },
      ventaja: {
        kicker: "TU VENTAJA",
        frase:
          "“Tu ventaja aparece cuando logras convertir sensibilidad en intuición estratégica humana.”",
        body:
          "Mientras otros gestionan tareas, tú gestionas vínculos y futuro al mismo tiempo. Lees el ambiente y anticipas su impacto. Eres puente con visión.",
      },
    },

    elcamino: {
      kicker: "EL CAMINO",
      titulo: "Las 4 etapas de evolución.",
      subtitle:
        "PLAY YOUR GAME™ no busca cambiar quién eres.\nBusca ayudarte a usar tu ventaja con intención.",
      etapas: [
        {
          numero: "01 / IDENTIFICACIÓN",
          titulo: "“Así juego naturalmente.”",
          body:
            "Lees el clima con el Delfín y miras hacia adelante con el Halcón. Cuidas el vínculo mientras anticipas lo que viene.",
          variant: "neutral",
        },
        {
          numero: "02 / CONCIENCIA",
          titulo: "“Mi fortaleza también puede jugar en mi contra.”",
          body:
            "Asumes que cuidar y anticipar al mismo tiempo te cuesta el presente. Empiezas a vivir emocionalmente escenarios que todavía no existen.",
          variant: "neutral",
        },
        {
          numero: "03 / REGULACIÓN",
          titulo: "“Necesito controlar mi modo sombra.”",
          body:
            "Detectas el momento exacto en el que cargas tensiones futuras o cuando te diluyes para evitar un conflicto que aún no llegó.",
          variant: "sombra",
        },
        {
          numero: "04 / EVOLUCIÓN",
          titulo: "“Ahora uso mi ventaja con madurez.”",
          body:
            "Aprendes a sentir sin desaparecer. Anticipas sin cargar el futuro y cuidas sin perderte en lo que podría pasar.",
          variant: "green",
        },
      ],
    },

    tension2: {
      kicker: "EL COSTO DE LA ANTICIPACIÓN",
      frase:
        "Tu manera de cuidar el camino\nempieza a sentirse como agotamiento silencioso.",
    },

    silencio: {
      kicker: "Y AQUÍ EMPIEZA EL PROBLEMA",
      line1: "No soportas la tensión emocional.",
      line2Highlight: "Y empiezas a vivirla antes de que exista.",
    },

    problema: {
      frase:
        "Si nadie está viendo el impacto humano…\nempiezas a sostenerlo tú.",
      footer: "Y poco a poco terminas cargando un futuro que todavía no llegó.",
    },

    modosombra: {
      kicker: "TENSIÓN PSICOLÓGICA",
      titulo: "EL MODO SOMBRA",
      rightLabel: "CÓMO TE DEGRADAS",
      quote:
        "“Empiezas a sentir problemas que todavía no existen…\nhasta que desapareces del presente.”",
      bloques: [
        {
          tag: "LA ABSORCIÓN",
          titulo: "Delfín Ansioso",
          quote: "“Sientes el conflicto antes de que exista.”",
          body:
            "Tu sensibilidad deja de filtrar el presente y empieza a vivir el futuro emocional. Cargas tensiones que todavía no llegaron y conversaciones que nadie tuvo. Lo que era intuición se vuelve ansiedad silenciosa.",
        },
        {
          tag: "LA ANTICIPACIÓN",
          titulo: "Halcón Abrumado",
          quote: "“Tu mente ya vivió tres escenarios que aún no pasaron.”",
          body:
            "Cuando tu Halcón se acelera, deja de orientar y empieza a fabricar futuros. Anticipas el daño, el conflicto, el desgaste… y sin darte cuenta empiezas a habitar emocionalmente algo que todavía no existe.",
        },
      ],
      resultado: {
        kicker: "EL RESULTADO REAL",
        body:
          "Terminas vacío, no por dar de más sino por cargar lo que aún no llegó. El Delfín no se cansa por cuidar, se agota por cuidar también el futuro. Tu fuerza se gasta sosteniendo emocionalmente algo que todavía nadie está viviendo.",
      },
    },

    tension3: {
      kicker: "TU VENTAJA SIN REGULACIÓN",
      frase: "Puede convertir tu intuición en agotamiento emocional.",
    },

    regulacion: {
      kicker: "REGULACIÓN",
      titulo: "Volver al presente sin dejar de sentir.",
      rightLabel: "MADUREZ ESTRATÉGICA",
      intro:
        "La evolución del DELFÍN no es sentir menos, es aprender a no cargar el futuro.",
      bloques: [
        {
          numero: "01",
          titulo: "HABITAR EL PRESENTE",
          badge: "ANCLAJE",
          body:
            "Lo que todavía no pasó no está pidiendo que lo sostengas. Tu reto es regresar al ahora cada vez que tu mente quiera vivir un futuro que aún no existe.",
        },
        {
          numero: "02",
          titulo: "SENTIR SIN ABSORBER",
          badge: "LÍMITE EMOCIONAL",
          body:
            "Notar el clima no es lo mismo que cargarlo. Aprende a registrar lo que sientes sin convertirlo en tu propia tensión y a observar sin diluirte.",
        },
        {
          numero: "03",
          titulo: "ANTICIPAR SIN HABITAR",
          badge: "CRITERIO",
          body:
            "Tu Halcón está para ver, no para mudarse al futuro. Apóyate en su visión para decidir hoy, no para vivir emocionalmente algo que todavía no llegó.",
        },
      ],
      slider: {
        kicker: "TU ESTADO ACTUAL",
        titulo: "¿Cómo estás jugando hoy?",
        subtitle: "Muévete entre absorción, balance y anticipación.",
        leftLabel: "DELFÍN ANSIOSO",
        centerLabel: "BALANCE",
        rightLabel: "HALCÓN ABRUMADO",
        states: [
          {
            max: 33,
            titulo: "DELFÍN ANSIOSO",
            texto:
              "Estás sintiendo tensiones que todavía no existen.\n\nLa anticipación empezó a costarte el presente.",
          },
          {
            max: 66,
            titulo: "BALANCE",
            texto:
              "Sientes y anticipas sin perderte.\n\nCuidas el presente mientras lees el camino.",
          },
          {
            max: 100,
            titulo: "HALCÓN ABRUMADO",
            texto:
              "Tu mente ya vivió escenarios que aún no pasaron.\n\nEmpezaste a desaparecer dentro de ellos.",
          },
        ],
      },
    },

    cierre: {
      kicker: "EVOLUCIÓN",
      titulo: "La presión no vino a destruir tu ventaja.",
      highlight: "Vino a enseñarte a usarla mejor.",
      subtitle:
        "No necesitas dejar de cuidar a los demás.\nNecesitas volver a ti.",
    },
  },

  delfin_lobo: {
    dominant: "DELFÍN",
    secondary: "LOBO",

    portada: {
      kicker: "PLAY YOUR GAME™",
      kickerRight: "DELFÍN dominante · LOBO secundario",
      quote:
        "Tu problema no es falta de empatía.\nEs evitar tanto el conflicto…\nque empiezas a traicionarte.",
      footerLeft: "La presión no cambia cómo juegas.\nRevela cómo juegas.",
      footerRight: "DELFÍN + LOBO",
    },

    tension1: {
      kicker: "LA PRESIÓN REVELA",
      frase:
        "Empiezas a evitar conversaciones difíciles…\nhasta que empiezas a desaparecer dentro de ellas.",
    },

    arquetipo: {
      kicker: "PARTE I",
      titulo: "TUS DOS FUERZAS",
      rightLabel: "CÓMO REACCIONAS",
      dominante: {
        numero: "01",
        titulo: "EL DELFÍN: Empatía y Vínculo",
        line:
          "Sostienes la calma del grupo… aunque por dentro algo no esté bien.",
        body:
          "Tu sensibilidad detecta el clima antes que las palabras. Notas la tensión, las miradas, lo que no se dice. Y casi sin pensarlo, te mueves para suavizar, para acercar, para que nadie se incomode. Tu fuerza es cuidar el vínculo; tu riesgo es cuidarlo a costa tuya.",
      },
      secundario: {
        numero: "02",
        titulo: "EL LOBO: Orden y Criterio",
        line: "Le da forma a tu manera de cuidar.",
        body:
          "El Lobo te da estructura para que tu cuidado no sea caos: reglas claras, método y previsibilidad. Pero cuando el Delfín se calla para no incomodar, el Lobo se vuelve cómplice y empieza a justificar el silencio con orden.",
      },
      ventaja: {
        kicker: "TU VENTAJA",
        frase:
          "“Tu ventaja aparece cuando logras cuidar el vínculo sin desaparecer de él.”",
        body:
          "Mientras otros cargan tensión, tú creas espacios donde se puede hablar. Sostienes con sensibilidad y enmarcas con criterio. Eres puente, no espejo.",
      },
    },

    elcamino: {
      kicker: "EL CAMINO",
      titulo: "Las 4 etapas de evolución.",
      subtitle:
        "PLAY YOUR GAME™ no busca cambiar quién eres.\nBusca ayudarte a usar tu ventaja con intención.",
      etapas: [
        {
          numero: "01 / IDENTIFICACIÓN",
          titulo: "“Así juego naturalmente.”",
          body:
            "Lees el clima con el Delfín y le pones marco con el Lobo. Cuidas el vínculo, suavizas la fricción y haces que el grupo respire.",
          variant: "neutral",
        },
        {
          numero: "02 / CONCIENCIA",
          titulo: "“Mi fortaleza también puede jugar en mi contra.”",
          body:
            "Asumes que cuidar el vínculo empieza a costarte tu voz. Empiezas a callar decisiones importantes para evitar incomodar a los demás.",
          variant: "neutral",
        },
        {
          numero: "03 / REGULACIÓN",
          titulo: "“Necesito controlar mi modo sombra.”",
          body:
            "Detectas el momento exacto en el que evitas una conversación que ya tocaba, o cuando tu Lobo justifica el silencio para no romper la armonía.",
          variant: "sombra",
        },
        {
          numero: "04 / EVOLUCIÓN",
          titulo: "“Ahora uso mi ventaja con madurez.”",
          body:
            "Aprendes a decir lo difícil sin perder el vínculo. Sostienes la tensión el tiempo necesario y dejas de pagar la paz con tu propia voz.",
          variant: "green",
        },
      ],
    },

    tension2: {
      kicker: "EL COSTO DE LA ARMONÍA",
      frase:
        "Tu manera de cuidar el vínculo\nempieza a sentirse como borrarte de él.",
    },

    silencio: {
      kicker: "Y AQUÍ EMPIEZA EL PROBLEMA",
      line1: "No soportas la tensión emocional.",
      line2Highlight: "Y empiezas a callar lo que sí importa.",
    },

    problema: {
      frase:
        "Si nadie está dispuesto a incomodar…\nempiezas a ceder tú.",
      footer: "Y poco a poco terminas postergando lo que tu propia voz necesitaba decir.",
    },

    modosombra: {
      kicker: "TENSIÓN PSICOLÓGICA",
      titulo: "EL MODO SOMBRA",
      rightLabel: "CÓMO TE DEGRADAS",
      quote:
        "“Tu necesidad de evitar tensión\nempieza a convertirse en autoabandono.”",
      bloques: [
        {
          tag: "LA ADAPTACIÓN",
          titulo: "Delfín Complaciente",
          quote: "“Dejaste de decir lo que necesitabas… para no romper el vínculo.”",
          body:
            "Te adaptas, suavizas, cedes. Y aunque el grupo respira, dentro tuyo se va apagando algo importante. Empiezas a postergarte sin darte cuenta y a confundir armonía con desaparición.",
        },
        {
          tag: "EL CONTROL",
          titulo: "Lobo Silencioso",
          quote: "“Tu orden empieza a justificar tu silencio.”",
          body:
            "Cuando el Delfín calla, el Lobo le pone forma: ‘no es el momento’, ‘no vale la pena’, ‘mejor después’. Una conversación que tocaba hoy se convierte en un peso que cargas tú solo.",
        },
      ],
      resultado: {
        kicker: "EL RESULTADO REAL",
        body:
          "Terminas vacío, no por dar de más sino por dejar de aparecer. El Delfín no se cansa por cuidar, se agota por cuidar sin que se le note la voz. Tu fuerza se gasta sosteniendo una paz que ya no te incluye.",
      },
    },

    tension3: {
      kicker: "TU VENTAJA SIN REGULACIÓN",
      frase: "Puede convertir tu sensibilidad en silencio.",
    },

    regulacion: {
      kicker: "REGULACIÓN",
      titulo: "Decir sin romper el vínculo.",
      rightLabel: "MADUREZ ESTRATÉGICA",
      intro:
        "La evolución del DELFÍN no es dejar de cuidar, es aprender a no callar lo que importa.",
      bloques: [
        {
          numero: "01",
          titulo: "NOMBRAR ANTES DE CEDER",
          badge: "VOZ PROPIA",
          body:
            "Lo que no nombras se acumula en silencio. Tu reto es decir lo que necesitas antes de que la armonía se vuelva costo y antes de que tu Lobo justifique callarlo.",
        },
        {
          numero: "02",
          titulo: "SOSTENER LA TENSIÓN",
          badge: "INCOMODIDAD",
          body:
            "No toda incomodidad es ruptura: a veces es solo verdad llegando tarde. Aprende a quedarte en la conversación difícil el tiempo que haga falta, sin huir hacia la calma.",
        },
        {
          numero: "03",
          titulo: "CUIDAR SIN DESAPARECER",
          badge: "CRITERIO",
          body:
            "El Lobo no está para apagar tu voz, está para protegerla. Apóyate en su orden para sostener lo que tu Delfín ya sabía y todavía no se atrevía a decir.",
        },
      ],
      slider: {
        kicker: "TU ESTADO ACTUAL",
        titulo: "¿Cómo estás jugando hoy?",
        subtitle: "Muévete entre complacencia, balance y silencio.",
        leftLabel: "DELFÍN COMPLACIENTE",
        centerLabel: "BALANCE",
        rightLabel: "LOBO SILENCIOSO",
        states: [
          {
            max: 33,
            titulo: "DELFÍN COMPLACIENTE",
            texto:
              "Estás cediendo para que nadie se incomode.\n\nLa paz empezó a costarte tu propia voz.",
          },
          {
            max: 66,
            titulo: "BALANCE",
            texto:
              "Cuidas el vínculo sin perderte.\n\nDices lo difícil cuando toca decirlo.",
          },
          {
            max: 100,
            titulo: "LOBO SILENCIOSO",
            texto:
              "Convertiste tu silencio en regla.\n\nTu orden empezó a justificar lo que ya no dices.",
          },
        ],
      },
    },

    cierre: {
      kicker: "EVOLUCIÓN",
      titulo: "La presión no vino a destruir tu ventaja.",
      highlight: "Vino a enseñarte a usarla mejor.",
      subtitle:
        "No necesitas dejar de ser DELFÍN.\nNecesitas aprender a no callarte.",
    },
  },

  leon_lobo: {
    dominant: "LEÓN",
    secondary: "LOBO",

    portada: {
      kicker: "PLAY YOUR GAME™",
      kickerRight: "LEÓN dominante · LOBO secundario",
      quote:
        "Tu problema no es falta de fuerza.\nEs sentir que todo depende de que tú lo muevas.",
      footerLeft: "La presión no cambia cómo juegas.\nRevela cómo juegas.",
      footerRight: "LEÓN + LOBO",
    },

    tension1: {
      kicker: "LA PRESIÓN REVELA",
      frase:
        "Cuando nadie mueve esto…\nempiezas a sentir que tienes que moverlo tú.",
    },

    arquetipo: {
      kicker: "PARTE I",
      titulo: "TUS DOS FUERZAS",
      rightLabel: "CÓMO REACCIONAS",
      dominante: {
        numero: "01",
        titulo: "EL LEÓN: Impulso y Fuerza",
        line:
          "Tu cuerpo entra en movimiento antes que tu mente… y no soportas ver que las cosas se quedan quietas.",
        body:
          "Cuando aparece presión, no te quedas pensando: mueves, avanzas y resuelves antes de que el resto reaccione. No naciste para esperar; naciste para empujar. Tu energía sube cuando hay urgencia, fricción o algo que destrabar. Y cuando todo se detiene, sientes que tienes que moverlo tú.",
      },
      secundario: {
        numero: "02",
        titulo: "EL LOBO: Orden y Criterio",
        line: "El caos te incomoda. Necesitas que tu fuerza tenga un porqué.",
        body:
          "Tu Lobo no frena al León; lo afina. Necesitas que lo que empujas tenga sentido y un orden detrás. Buscas método, claridad y un paso a paso que sostenga la intensidad. Por eso tu fuerza no se siente arbitraria: se siente con criterio.",
      },
      ventaja: {
        kicker: "TU VENTAJA",
        frase:
          "“Tu ventaja aparece cuando logras convertir intensidad en ejecución con criterio.”",
        body:
          "Mientras otros todavía piensan, tú ya estás moviendo algo. Y lo estás moviendo con dirección. Eres fuerza que decide y movimiento que organiza.",
      },
    },

    elcamino: {
      kicker: "EL CAMINO",
      titulo: "Las 4 etapas de evolución.",
      subtitle:
        "PLAY YOUR GAME™ no busca cambiar quién eres.\nBusca ayudarte a usar tu ventaja con intención.",
      etapas: [
        {
          numero: "01 / IDENTIFICACIÓN",
          titulo: "“Así juego naturalmente.”",
          body:
            "Tu cuerpo entra en movimiento antes que tu mente. El LEÓN acelera y el LOBO organiza, en una zona cómoda donde siempre hay algo que mover.",
          variant: "neutral",
        },
        {
          numero: "02 / CONCIENCIA",
          titulo: "“Mi fortaleza también puede jugar en mi contra.”",
          body:
            "Asumes que la lentitud te desespera. Cuando nadie actúa, tu presión interna empieza a subir y tu fuerza empieza a sentirse como urgencia.",
          variant: "neutral",
        },
        {
          numero: "03 / REGULACIÓN",
          titulo: "“Necesito controlar mi modo sombra.”",
          body:
            "Detectas el momento exacto en el que empiezas a reaccionar demasiado rápido o cuando avanzas sin escuchar porque necesitas sentir movimiento inmediato.",
          variant: "sombra",
        },
        {
          numero: "04 / EVOLUCIÓN",
          titulo: "“Ahora uso mi ventaja con madurez.”",
          body:
            "Aprendes a avanzar sin atropellar. Tu fuerza deja de sentirse como presión y empieza a sentirse como liderazgo real.",
          variant: "green",
        },
      ],
    },

    tension2: {
      kicker: "EL COSTO DE LA VELOCIDAD",
      frase:
        "Tu velocidad empieza a sentirse como presión\nen un equipo que todavía no llegó ahí.",
    },

    silencio: {
      kicker: "Y AQUÍ EMPIEZA EL PROBLEMA",
      line1: "No soportas la lentitud.",
      line2Highlight: "Y empiezas a empujar antes de tiempo.",
    },

    problema: {
      frase:
        "Si nadie mueve esto…\nempiezas a sentir que tienes que moverlo tú.",
      footer: "Y poco a poco terminas avanzando sin esperar a nadie.",
    },

    modosombra: {
      kicker: "TENSIÓN PSICOLÓGICA",
      titulo: "EL MODO SOMBRA",
      rightLabel: "CÓMO TE DEGRADAS",
      quote:
        "“Tu intensidad quiere mover todo.\nY poco a poco… empiezas a aplastar a quienes no avanzan a tu velocidad.”",
      bloques: [
        {
          tag: "EL IMPULSO",
          titulo: "León Impulsivo",
          quote: "“La lentitud empieza a desesperarte… y reaccionas antes de entender.”",
          body:
            "Tu cuerpo entra en modo mover antes que tu mente. Empiezas a empujar decisiones y a resolver antes de escuchar. Las personas dejan de sentir liderazgo y empiezan a sentir presión.",
        },
        {
          tag: "LA RIGIDEZ",
          titulo: "Lobo Rígido",
          quote: "“Solo confías en el camino que ya conoces.”",
          body:
            "Cuando ya empujaste demasiado, tu Lobo se cierra. Te aferras a tu propio método y dejas de considerar otras formas. Lo que empezó como criterio termina convertido en muro.",
        },
      ],
      resultado: {
        kicker: "EL RESULTADO REAL",
        body:
          "Terminas agotado, no por hacer demasiado sino por reaccionar demasiado. El León no se cansa por mover, se agota por mover sin esperar a nadie. Tu fuerza se gasta intentando que el mundo lleve tu ritmo.",
      },
    },

    tension3: {
      kicker: "TU VENTAJA SIN REGULACIÓN",
      frase: "Puede convertir tu fuerza en presión silenciosa.",
    },

    regulacion: {
      kicker: "REGULACIÓN",
      titulo: "Bajar velocidad sin perder fuerza.",
      rightLabel: "MADUREZ ESTRATÉGICA",
      intro:
        "La evolución del LEÓN no es bajar la fuerza, es aprender a no reaccionar tan rápido.",
      bloques: [
        {
          numero: "01",
          titulo: "PAUSAR ANTES DE EMPUJAR",
          badge: "CONTROL DE IMPULSO",
          body:
            "No toda tensión necesita resolverse inmediatamente; a veces la presión baja sola si esperas un segundo más. Tu reto no es bajar fuerza, es aprender a no reaccionar al primer impulso.",
        },
        {
          numero: "02",
          titulo: "ESCUCHAR ANTES DE RESOLVER",
          badge: "TOLERANCIA",
          body:
            "Tu mente entra en modo resolver demasiado rápido, pero no todo problema necesita una respuesta inmediata. A veces lo único que el otro necesita es sentirse escuchado antes que solucionado.",
        },
        {
          numero: "03",
          titulo: "AVANZAR SIN ATROPELLAR",
          badge: "CRITERIO",
          body:
            "No necesitas mover todo a tu ritmo para liderar a quienes vienen contigo. Tu evolución aparece cuando aprendes a soltar velocidad sin perder dirección.",
        },
      ],
      slider: {
        kicker: "TU ESTADO ACTUAL",
        titulo: "¿Cómo estás jugando hoy?",
        subtitle: "Muévete entre impulso, regulación y rigidez.",
        leftLabel: "LEÓN IMPULSIVO",
        centerLabel: "BALANCE",
        rightLabel: "LOBO RÍGIDO",
        states: [
          {
            max: 33,
            titulo: "LEÓN IMPULSIVO",
            texto:
              "Estás reaccionando demasiado rápido.\n\nLa urgencia empezó a controlar tu forma de moverte.",
          },
          {
            max: 66,
            titulo: "BALANCE",
            texto:
              "Tu fuerza tiene criterio.\n\nEmpujas cuando hace falta.\nEsperas cuando hace falta.",
          },
          {
            max: 100,
            titulo: "LOBO RÍGIDO",
            texto:
              "Reaccionaste tanto que ahora te cerraste.\n\nSolo confías en tu propio método.",
          },
        ],
      },
    },

    cierre: {
      kicker: "EVOLUCIÓN",
      titulo: "La presión no vino a destruir tu ventaja.",
      highlight: "Vino a enseñarte a usarla mejor.",
      subtitle:
        "No necesitas dejar de ser LEÓN.\nNecesitas aprender a escuchar.",
    },
  },

  leon_halcon: {
    dominant: "LEÓN",
    secondary: "HALCÓN",

    portada: {
      kicker: "PLAY YOUR GAME™",
      kickerRight: "LEÓN dominante · HALCÓN secundario",
      quote:
        "Tu problema no es falta de fuerza.\nEs no soportar que las cosas estén quietas…\ny terminar moviendo todo tú.",
      footerLeft: "La presión no cambia cómo juegas.\nRevela cómo juegas.",
      footerRight: "LEÓN + HALCÓN",
    },

    tension1: {
      kicker: "LA PRESIÓN REVELA",
      frase:
        "Si nadie está moviendo esto…\nempiezas a sentir que tienes que moverlo tú.",
    },

    arquetipo: {
      kicker: "PARTE I",
      titulo: "TUS DOS FUERZAS",
      rightLabel: "CÓMO REACCIONAS",
      dominante: {
        numero: "01",
        titulo: "EL LEÓN: Impulso y Movimiento",
        line:
          "Tu cuerpo entra en movimiento antes que tu mente.",
        body:
          "Cuando aparece presión, no te quedas pensando: mueves, empujas y resuelves antes que el resto reaccione. La lentitud te desespera y la quietud te consume. Mientras otros todavía analizan, tú ya estás avanzando. Tu fuerza no es agresión, es necesidad de mover algo.",
      },
      secundario: {
        numero: "02",
        titulo: "EL HALCÓN: Visión y Dirección",
        line: "Le da rumbo a tu fuerza.",
        body:
          "Tu Halcón no domina tu energía, la direcciona. No solo quieres mover algo, quieres saber hacia dónde. Tu mente intenta adelantarse al próximo paso para que tu impulso tenga sentido. Por eso tu fuerza no se siente arbitraria: avanza con rumbo.",
      },
      ventaja: {
        kicker: "TU VENTAJA",
        frase:
          "“Tu ventaja aparece cuando logras convertir intensidad en movimiento con dirección.”",
        body:
          "Mientras otros todavía dudan, tú ya estás avanzando. Y mientras avanzas, ya estás viendo el siguiente paso. Eres fuerza que se mueve y se orienta.",
      },
    },

    elcamino: {
      kicker: "EL CAMINO",
      titulo: "Las 4 etapas de evolución.",
      subtitle:
        "PLAY YOUR GAME™ no busca cambiar quién eres.\nBusca ayudarte a usar tu ventaja con intención.",
      etapas: [
        {
          numero: "01 / IDENTIFICACIÓN",
          titulo: "“Así juego naturalmente.”",
          body:
            "Tu cuerpo entra en movimiento antes que tu mente. El León empuja y el Halcón le da rumbo a lo que mueves.",
          variant: "neutral",
        },
        {
          numero: "02 / CONCIENCIA",
          titulo: "“Mi fortaleza también puede jugar en mi contra.”",
          body:
            "Asumes que la lentitud te desespera y que tu velocidad empieza a sentirse como presión para las personas que vienen contigo.",
          variant: "neutral",
        },
        {
          numero: "03 / REGULACIÓN",
          titulo: "“Necesito controlar mi modo sombra.”",
          body:
            "Detectas el momento exacto en el que empiezas a reaccionar demasiado rápido o cuando exiges a otros que avancen a tu ritmo.",
          variant: "sombra",
        },
        {
          numero: "04 / EVOLUCIÓN",
          titulo: "“Ahora uso mi ventaja con madurez.”",
          body:
            "Aprendes a empujar sin atropellar. Tu fuerza deja de sentirse como urgencia y empieza a sentirse como liderazgo real.",
          variant: "green",
        },
      ],
    },

    tension2: {
      kicker: "EL COSTO DE LA VELOCIDAD",
      frase:
        "Tu velocidad empieza a sentirse como presión\nen las personas que vienen contigo.",
    },

    silencio: {
      kicker: "Y AQUÍ EMPIEZA EL PROBLEMA",
      line1: "No soportas la lentitud.",
      line2Highlight: "Y empiezas a empujar antes de tiempo.",
    },

    problema: {
      frase: "Empiezas a sentir que si tú no empujas…\nnada se mueve.",
      footer: "Y poco a poco empiezas a cargar todo solo.",
    },

    modosombra: {
      kicker: "TENSIÓN PSICOLÓGICA",
      titulo: "EL MODO SOMBRA",
      rightLabel: "CÓMO TE DEGRADAS",
      quote:
        "“Tu intensidad quiere mover todo.\nY poco a poco… empiezas a aplastar a quienes no avanzan a tu velocidad.”",
      bloques: [
        {
          tag: "EL IMPULSO",
          titulo: "León Impulsivo",
          quote: "“La lentitud empieza a desesperarte… y reaccionas antes de entender.”",
          body:
            "Tu cuerpo entra en modo mover antes que tu mente. Empiezas a empujar decisiones y a resolver antes de escuchar. Las personas dejan de sentir liderazgo y empiezan a sentir urgencia.",
        },
        {
          tag: "LA DESCONEXIÓN",
          titulo: "Halcón Adelantado",
          quote: "“Tu cabeza ya está en la siguiente jugada.”",
          body:
            "Cuando se acumula demasiada presión, tu mente se va hacia adelante. Empiezas a pensar en lo que falta antes de terminar lo que estás haciendo, y poco a poco dejas de estar realmente presente para quienes vienen contigo.",
        },
      ],
      resultado: {
        kicker: "EL RESULTADO REAL",
        body:
          "Terminas agotado, no por hacer demasiado sino por reaccionar demasiado. El León no se cansa por mover, se agota por mover sin esperar a nadie. Y mientras más presión aparece, más velocidad usas para intentar recuperar el control.",
      },
    },

    tension3: {
      kicker: "TU VENTAJA SIN REGULACIÓN",
      frase: "Puede convertir tu intensidad en presión silenciosa.",
    },

    regulacion: {
      kicker: "REGULACIÓN",
      titulo: "Bajar velocidad sin perder fuerza.",
      rightLabel: "MADUREZ ESTRATÉGICA",
      intro:
        "La evolución del LEÓN no es bajar la fuerza, es aprender a no reaccionar tan rápido.",
      bloques: [
        {
          numero: "01",
          titulo: "PAUSAR ANTES DE EMPUJAR",
          badge: "CONTROL DE IMPULSO",
          body:
            "No toda tensión necesita resolverse inmediatamente; a veces la presión baja sola si esperas un segundo más. Tu reto es no reaccionar al primer impulso.",
        },
        {
          numero: "02",
          titulo: "ESCUCHAR ANTES DE RESOLVER",
          badge: "TOLERANCIA",
          body:
            "Tu energía entra en modo mover demasiado rápido, pero no todo problema necesita una respuesta inmediata. A veces lo único que el otro necesita es sentirse escuchado.",
        },
        {
          numero: "03",
          titulo: "DEJAR ESPACIO A OTROS RITMOS",
          badge: "PRESENCIA",
          body:
            "No necesitas que todo avance a tu velocidad para liderar. Tu evolución aparece cuando aprendes a esperar sin perder dirección y dejas que otros también muevan algo.",
        },
      ],
      slider: {
        kicker: "TU ESTADO ACTUAL",
        titulo: "¿Cómo estás jugando hoy?",
        subtitle: "Muévete entre impulso, balance y desconexión.",
        leftLabel: "LEÓN IMPULSIVO",
        centerLabel: "BALANCE",
        rightLabel: "HALCÓN ADELANTADO",
        states: [
          {
            max: 33,
            titulo: "LEÓN IMPULSIVO",
            texto:
              "Estás reaccionando demasiado rápido.\n\nLa urgencia empezó a controlar tu forma de liderar.",
          },
          {
            max: 66,
            titulo: "BALANCE",
            texto:
              "Tu fuerza tiene rumbo.\n\nEmpujas cuando hace falta.\nPausas cuando hace falta.",
          },
          {
            max: 100,
            titulo: "HALCÓN ADELANTADO",
            texto:
              "Tu cabeza ya está adelante.\n\nPero las personas todavía necesitan sentirte aquí.",
          },
        ],
      },
    },

    cierre: {
      kicker: "EVOLUCIÓN",
      titulo: "La presión no vino a destruir tu fuerza.",
      highlight: "Vino a enseñarte a pausar.",
      subtitle:
        "No necesitas dejar de ser LEÓN.\nNecesitas aprender a esperar.",
    },
  },

  lobo_halcon: {
    dominant: "LOBO",
    secondary: "HALCÓN",

    portada: {
      kicker: "PLAY YOUR GAME™",
      kickerRight: "LOBO dominante · HALCÓN secundario",
      quote:
        "Tu problema no es falta de claridad.\nEs esperar tanta certeza…\nque terminas perdiendo movimiento.",
      footerLeft: "La presión no cambia cómo juegas.\nRevela cómo juegas.",
      footerRight: "LOBO + HALCÓN",
    },

    tension1: {
      kicker: "LA PRESIÓN REVELA",
      frase:
        "Cuando todo se tensa…\nempiezas a pensar más y a moverte menos.",
    },

    arquetipo: {
      kicker: "PARTE I",
      titulo: "TUS DOS FUERZAS",
      rightLabel: "CÓMO REACCIONAS",
      dominante: {
        numero: "01",
        titulo: "EL LOBO: Estructura y Criterio",
        line:
          "Necesitas entender antes de actuar.",
        body:
          "Antes de mover algo, lo organizas: lees el contexto, ordenas la información y diseñas el camino. Te incomoda el caos y desconfías de los golpes de suerte. Buscas método, datos y un paso a paso que sostenga la decisión. Tu fuerza no es velocidad, es claridad.",
      },
      secundario: {
        numero: "02",
        titulo: "EL HALCÓN: Visión y Anticipación",
        line: "Le da escenarios a tu lógica.",
        body:
          "Tu Halcón mira hacia adelante: los riesgos, los movimientos, lo que aún no pasó. No solo entiendes el presente, también ves cómo terminará. Por eso tu análisis no se queda quieto: se proyecta y construye futuro antes de que el resto vea el problema.",
      },
      ventaja: {
        kicker: "TU VENTAJA",
        frase:
          "“Tu ventaja aparece cuando logras convertir profundidad mental en claridad estratégica.”",
        body:
          "Mientras otros reaccionan, tú entiendes. Y mientras entiendes, ya estás viendo el siguiente movimiento. Eres dirección antes de que el resto vea el problema.",
      },
    },

    elcamino: {
      kicker: "EL CAMINO",
      titulo: "Las 4 etapas de evolución.",
      subtitle:
        "PLAY YOUR GAME™ no busca cambiar quién eres.\nBusca ayudarte a usar tu ventaja con intención.",
      etapas: [
        {
          numero: "01 / IDENTIFICACIÓN",
          titulo: "“Así juego naturalmente.”",
          body:
            "Lees el sistema con el Lobo y proyectas escenarios con el Halcón. Cuidas la decisión antes de moverla, en una zona cómoda de criterio y visión.",
          variant: "neutral",
        },
        {
          numero: "02 / CONCIENCIA",
          titulo: "“Mi fortaleza también puede jugar en mi contra.”",
          body:
            "Asumes que pensar y anticipar al mismo tiempo te aleja del movimiento. Empiezas a vivir más en posibilidades que en acción real.",
          variant: "neutral",
        },
        {
          numero: "03 / REGULACIÓN",
          titulo: "“Necesito controlar mi modo sombra.”",
          body:
            "Detectas el momento exacto en el que tu mente fabrica escenarios para no decidir, o cuando exiges certeza absoluta antes de avanzar.",
          variant: "sombra",
        },
        {
          numero: "04 / EVOLUCIÓN",
          titulo: "“Ahora uso mi ventaja con madurez.”",
          body:
            "Aprendes a moverte sin tenerlo todo claro. Decides con criterio incompleto y dejas de pagar la claridad con tu propio movimiento.",
          variant: "green",
        },
      ],
    },

    tension2: {
      kicker: "EL COSTO DE LA CLARIDAD",
      frase:
        "Tu manera de buscar claridad\nempieza a sentirse como inmovilidad estratégica.",
    },

    silencio: {
      kicker: "Y AQUÍ EMPIEZA EL PROBLEMA",
      line1: "No soportas decidir sin certeza.",
      line2Highlight: "Y empiezas a quedarte pensando.",
    },

    problema: {
      frase:
        "Si nadie está viendo lo que tú ves…\nempiezas a sostenerlo todo en tu cabeza.",
      footer: "Y poco a poco terminas analizando un futuro que nadie está ejecutando.",
    },

    modosombra: {
      kicker: "TENSIÓN PSICOLÓGICA",
      titulo: "EL MODO SOMBRA",
      rightLabel: "CÓMO TE DEGRADAS",
      quote:
        "“Tu mente sigue proyectando posibilidades…\nmientras el presente se queda quieto.”",
      bloques: [
        {
          tag: "LA PARÁLISIS",
          titulo: "Lobo Bloqueado",
          quote: "“Esperas claridad absoluta antes de mover.”",
          body:
            "Tu necesidad de entenderlo todo deja de proteger la decisión y empieza a impedirla. Acumulas datos, vuelves al mismo análisis y postergas movimientos importantes. Lo que era criterio se vuelve trampa mental.",
        },
        {
          tag: "LA PROYECCIÓN",
          titulo: "Halcón Infinito",
          quote: "“Tu mente vivió diez escenarios antes de mover uno.”",
          body:
            "Cuando tu Halcón se acelera, deja de orientar y empieza a fabricar futuros sin parar. Proyectas riesgos, variables y consecuencias… mientras el presente sigue quieto y sin avanzar.",
        },
      ],
      resultado: {
        kicker: "EL RESULTADO REAL",
        body:
          "Terminas frustrado, no por no saber sino por no moverte. El Lobo no se cansa por pensar, se agota por esperar tanta certeza que termina perdiendo movimiento. Tu fuerza se gasta entendiendo lo que el resto ya está ejecutando.",
      },
    },

    tension3: {
      kicker: "TU VENTAJA SIN REGULACIÓN",
      frase: "Puede convertir tu claridad en parálisis estratégica.",
    },

    regulacion: {
      kicker: "REGULACIÓN",
      titulo: "Avanzar sin tenerlo todo claro.",
      rightLabel: "MADUREZ ESTRATÉGICA",
      intro:
        "La evolución del LOBO no es pensar menos, es aprender a moverse antes de tener certeza total.",
      bloques: [
        {
          numero: "01",
          titulo: "DECIDIR CON LO QUE HAY",
          badge: "MOVIMIENTO",
          body:
            "No toda decisión necesita información completa para empezar. Tu reto es moverte con el 70% de la claridad y permitir que el resto aparezca avanzando.",
        },
        {
          numero: "02",
          titulo: "TOLERAR LA IMPERFECCIÓN",
          badge: "CRITERIO INCOMPLETO",
          body:
            "Un movimiento imperfecto avanzado vale más que un plan perfecto detenido. Aprende a soltar la búsqueda de certeza absoluta antes de actuar.",
        },
        {
          numero: "03",
          titulo: "VOLVER A LA ACCIÓN",
          badge: "PRESENTE",
          body:
            "Tu Halcón está para ver, no para vivir en el futuro. Apóyate en sus escenarios para decidir hoy, no para esperar mañana.",
        },
      ],
      slider: {
        kicker: "TU ESTADO ACTUAL",
        titulo: "¿Cómo estás jugando hoy?",
        subtitle: "Muévete entre parálisis, balance y proyección.",
        leftLabel: "LOBO BLOQUEADO",
        centerLabel: "BALANCE",
        rightLabel: "HALCÓN INFINITO",
        states: [
          {
            max: 33,
            titulo: "LOBO BLOQUEADO",
            texto:
              "Estás esperando claridad que no llegará sola.\n\nEl análisis empezó a costarte el movimiento.",
          },
          {
            max: 66,
            titulo: "BALANCE",
            texto:
              "Piensas y te mueves sin perder ritmo.\n\nDecides con criterio sin esperar certeza absoluta.",
          },
          {
            max: 100,
            titulo: "HALCÓN INFINITO",
            texto:
              "Tu mente sigue proyectando escenarios.\n\nMientras el presente se queda quieto.",
          },
        ],
      },
    },

    cierre: {
      kicker: "EVOLUCIÓN",
      titulo: "La presión no vino a destruir tu ventaja.",
      highlight: "Vino a enseñarte a usarla mejor.",
      subtitle:
        "No necesitas entenderlo todo antes de avanzar.\nNecesitas empezar.",
    },
  },

  lobo_delfin: {
    dominant: "LOBO",
    secondary: "DELFÍN",

    portada: {
      kicker: "PLAY YOUR GAME™",
      kickerRight: "LOBO dominante · DELFÍN secundario",
      quote:
        "Tu problema no es falta de comprensión.\nEs entender tanto a los demás…\nque empiezas a dejar de escucharte a ti.",
      footerLeft: "La presión no cambia cómo juegas.\nRevela cómo juegas.",
      footerRight: "LOBO + DELFÍN",
    },

    tension1: {
      kicker: "LA PRESIÓN REVELA",
      frase:
        "Cuando todo se tensa…\nempiezas a cargar conversaciones que nunca terminas teniendo.",
    },

    arquetipo: {
      kicker: "PARTE I",
      titulo: "TUS DOS FUERZAS",
      rightLabel: "CÓMO REACCIONAS",
      dominante: {
        numero: "01",
        titulo: "EL LOBO: Criterio y Profundidad",
        line:
          "Piensas antes de mover, y observas antes de decir.",
        body:
          "Tu Lobo lee el contexto, ordena la información y diseñas la decisión desde la calma. Te incomoda el caos y desconfías de los impulsos. Por eso transmites contención: las personas sienten que pueden hablar contigo porque no reaccionas, entiendes.",
      },
      secundario: {
        numero: "02",
        titulo: "EL DELFÍN: Empatía y Vínculo",
        line: "Le da sensibilidad a tu análisis.",
        body:
          "Tu Delfín lee el clima emocional: dónde hay tensión, dónde alguien está incómodo, qué se está callando. No solo entiendes lo que pasa, sientes lo que hay debajo. Por eso tu criterio no se mueve sin vínculo: piensa profundo y cuida humano.",
      },
      ventaja: {
        kicker: "TU VENTAJA",
        frase:
          "“Tu ventaja aparece cuando logras convertir profundidad mental en comprensión humana real.”",
        body:
          "Mientras otros reaccionan, tú escuchas. Y mientras escuchas, ya estás entendiendo lo que no se dijo. Eres criterio con vínculo.",
      },
    },

    elcamino: {
      kicker: "EL CAMINO",
      titulo: "Las 4 etapas de evolución.",
      subtitle:
        "PLAY YOUR GAME™ no busca cambiar quién eres.\nBusca ayudarte a usar tu ventaja con intención.",
      etapas: [
        {
          numero: "01 / IDENTIFICACIÓN",
          titulo: "“Así juego naturalmente.”",
          body:
            "Lees el sistema con el Lobo y sientes el clima con el Delfín. Cuidas la decisión y el vínculo al mismo tiempo, desde la calma.",
          variant: "neutral",
        },
        {
          numero: "02 / CONCIENCIA",
          titulo: "“Mi fortaleza también puede jugar en mi contra.”",
          body:
            "Asumes que entender a todos te aleja de ti. Empiezas a postergar conversaciones difíciles para no romper la armonía.",
          variant: "neutral",
        },
        {
          numero: "03 / REGULACIÓN",
          titulo: "“Necesito controlar mi modo sombra.”",
          body:
            "Detectas el momento exacto en el que racionalizas una emoción para no decirla, o cuando te adaptas tanto que dejas de aparecer en la decisión.",
          variant: "sombra",
        },
        {
          numero: "04 / EVOLUCIÓN",
          titulo: "“Ahora uso mi ventaja con madurez.”",
          body:
            "Aprendes a expresar antes de saturarte. Dices lo difícil sin perder el vínculo y dejas de pagar la armonía con tu propia voz.",
          variant: "green",
        },
      ],
    },

    tension2: {
      kicker: "EL COSTO DE LA COMPRENSIÓN",
      frase:
        "Tu manera de sostener a los demás\nempieza a sentirse como agotamiento silencioso.",
    },

    silencio: {
      kicker: "Y AQUÍ EMPIEZA EL PROBLEMA",
      line1: "No soportas la tensión emocional.",
      line2Highlight: "Y empiezas a racionalizar lo que sí importa.",
    },

    problema: {
      frase:
        "Si nadie está dispuesto a incomodar…\nempiezas a entenderlo tú.",
      footer: "Y poco a poco terminas postergando lo que tu propia voz necesitaba decir.",
    },

    modosombra: {
      kicker: "TENSIÓN PSICOLÓGICA",
      titulo: "EL MODO SOMBRA",
      rightLabel: "CÓMO TE DEGRADAS",
      quote:
        "“Empiezas a entender tanto a los demás…\nque dejas de escucharte a ti.”",
      bloques: [
        {
          tag: "LA SOBRECARGA",
          titulo: "Lobo Saturado",
          quote: "“Cargas conversaciones que nunca terminas teniendo.”",
          body:
            "Tu mente sigue dándole vueltas a lo que tendrías que decir, mientras tu Delfín sigue evitando romper la armonía. Acumulas escenarios, justificaciones y silencios. Lo que era criterio se vuelve peso interno.",
        },
        {
          tag: "LA ADAPTACIÓN",
          titulo: "Delfín Complaciente",
          quote: "“Dejaste de decir lo que necesitabas… para no romper el vínculo.”",
          body:
            "Cuando se acumula tensión, te adaptas en silencio: suavizas, cedes, postergas. Y aunque el grupo respira, dentro tuyo se va apagando algo importante. La armonía empieza a costarte tu propia voz.",
        },
      ],
      resultado: {
        kicker: "EL RESULTADO REAL",
        body:
          "Terminas vacío, no por sentir de más sino por sostener en silencio. El Lobo no se cansa por pensar, se agota por cargar conversaciones que nunca dijo. Tu fuerza se gasta entendiendo a todos menos a ti.",
      },
    },

    tension3: {
      kicker: "TU VENTAJA SIN REGULACIÓN",
      frase: "Puede convertir tu profundidad en silencio.",
    },

    regulacion: {
      kicker: "REGULACIÓN",
      titulo: "Decir sin desaparecer.",
      rightLabel: "MADUREZ ESTRATÉGICA",
      intro:
        "La evolución del LOBO no es entender menos, es aprender a no callar lo que importa.",
      bloques: [
        {
          numero: "01",
          titulo: "EXPRESAR ANTES DE SATURARTE",
          badge: "VOZ PROPIA",
          body:
            "Lo que no nombras se acumula en silencio. Tu reto es decirlo cuando todavía es manejable y no cuando ya pesa demasiado en tu cabeza.",
        },
        {
          numero: "02",
          titulo: "TOLERAR LA TENSIÓN",
          badge: "INCOMODIDAD",
          body:
            "No toda incomodidad es ruptura, a veces es solo verdad llegando tarde. Aprende a sostener la conversación difícil sin huir hacia la armonía.",
        },
        {
          numero: "03",
          titulo: "DECIDIR AUNQUE INCOMODE",
          badge: "CRITERIO HUMANO",
          body:
            "Tu Delfín no está para apagar tu criterio, sino para humanizarlo. Decide con la profundidad del Lobo y la sensibilidad del Delfín, sin postergar lo que ya viste.",
        },
      ],
      slider: {
        kicker: "TU ESTADO ACTUAL",
        titulo: "¿Cómo estás jugando hoy?",
        subtitle: "Muévete entre saturación, balance y silencio.",
        leftLabel: "LOBO SATURADO",
        centerLabel: "BALANCE",
        rightLabel: "DELFÍN COMPLACIENTE",
        states: [
          {
            max: 33,
            titulo: "LOBO SATURADO",
            texto:
              "Estás cargando conversaciones que nunca dijiste.\n\nEl análisis empezó a pesar más que el movimiento.",
          },
          {
            max: 66,
            titulo: "BALANCE",
            texto:
              "Piensas y cuidas sin perderte.\n\nDices lo difícil sin romper el vínculo.",
          },
          {
            max: 100,
            titulo: "DELFÍN COMPLACIENTE",
            texto:
              "Te adaptaste para que nadie se incomode.\n\nLa armonía empezó a costarte tu propia voz.",
          },
        ],
      },
    },

    cierre: {
      kicker: "EVOLUCIÓN",
      titulo: "La presión no vino a destruir tu ventaja.",
      highlight: "Vino a enseñarte a usarla mejor.",
      subtitle:
        "No necesitas evitar toda tensión.\nNecesitas dejar de desaparecer dentro de ella.",
    },
  },

  halcon_delfin: {
    dominant: "HALCÓN",
    secondary: "DELFÍN",

    portada: {
      kicker: "PLAY YOUR GAME™",
      kickerRight: "HALCÓN dominante · DELFÍN secundario",
      quote:
        "Tu problema no es falta de visión.\nEs vivir emocionalmente lo que aún no pasa…\nhasta perderte dentro de él.",
      footerLeft: "La presión no cambia cómo juegas.\nRevela cómo juegas.",
      footerRight: "HALCÓN + DELFÍN",
    },

    tension1: {
      kicker: "LA PRESIÓN REVELA",
      frase:
        "Cuando todo se tensa…\nempiezas a sentir un futuro que todavía no llegó.",
    },

    arquetipo: {
      kicker: "PARTE I",
      titulo: "TUS DOS FUERZAS",
      rightLabel: "CÓMO REACCIONAS",
      dominante: {
        numero: "01",
        titulo: "EL HALCÓN: Visión y Anticipación",
        line:
          "Tu mente ya está adelante… mientras el resto sigue en el presente.",
        body:
          "Tu Halcón mira hacia adelante: los riesgos, los escenarios, los movimientos que aún no pasaron. Mientras otros viven el problema de hoy, tú ya estás proyectando el de mañana. No solo entiendes la dirección, también ves cómo afectará a las personas que vienen contigo.",
      },
      secundario: {
        numero: "02",
        titulo: "EL DELFÍN: Sensibilidad y Vínculo",
        line: "Le da humanidad a tu visión.",
        body:
          "Tu Delfín lee el clima emocional: dónde hay tensión, dónde hay miedo, dónde algo se está quebrando. Por eso tu visión no se mueve sola: anticipa con sentido y dirige cuidando a quienes vienen contigo.",
      },
      ventaja: {
        kicker: "TU VENTAJA",
        frase:
          "“Tu ventaja aparece cuando logras convertir visión en intuición estratégica humana.”",
        body:
          "Mientras otros gestionan el presente, tú anticipas el futuro y su impacto emocional. Ves dirección con vínculo. Eres rumbo con sensibilidad.",
      },
    },

    elcamino: {
      kicker: "EL CAMINO",
      titulo: "Las 4 etapas de evolución.",
      subtitle:
        "PLAY YOUR GAME™ no busca cambiar quién eres.\nBusca ayudarte a usar tu ventaja con intención.",
      etapas: [
        {
          numero: "01 / IDENTIFICACIÓN",
          titulo: "“Así juego naturalmente.”",
          body:
            "Lees el futuro con el Halcón y sientes el impacto con el Delfín. Cuidas dirección y vínculo al mismo tiempo.",
          variant: "neutral",
        },
        {
          numero: "02 / CONCIENCIA",
          titulo: "“Mi fortaleza también puede jugar en mi contra.”",
          body:
            "Asumes que vivir adelante te aleja del ahora. Empiezas a anticipar emocionalmente escenarios que todavía no ocurren.",
          variant: "neutral",
        },
        {
          numero: "03 / REGULACIÓN",
          titulo: "“Necesito controlar mi modo sombra.”",
          body:
            "Detectas el momento exacto en el que cargas emocionalmente un futuro que aún no llegó o cuando te diluyes para evitar un conflicto que aún no nació.",
          variant: "sombra",
        },
        {
          numero: "04 / EVOLUCIÓN",
          titulo: "“Ahora uso mi ventaja con madurez.”",
          body:
            "Aprendes a anticipar sin habitar. Ves el futuro sin desaparecer del presente y cuidas sin perderte dentro de lo que sientes.",
          variant: "green",
        },
      ],
    },

    tension2: {
      kicker: "EL COSTO DE LA ANTICIPACIÓN",
      frase:
        "Tu manera de cuidar la dirección\nempieza a sentirse como desaparición silenciosa.",
    },

    silencio: {
      kicker: "Y AQUÍ EMPIEZA EL PROBLEMA",
      line1: "No soportas la tensión futura.",
      line2Highlight: "Y empiezas a vivirla antes de que exista.",
    },

    problema: {
      frase:
        "Si nadie está viendo el impacto humano del camino…\nempiezas a sostenerlo tú.",
      footer: "Y poco a poco terminas viviendo emocionalmente algo que aún no llegó.",
    },

    modosombra: {
      kicker: "TENSIÓN PSICOLÓGICA",
      titulo: "EL MODO SOMBRA",
      rightLabel: "CÓMO TE DEGRADAS",
      quote:
        "“Empiezas a sentir problemas que todavía no existen…\nhasta que desapareces del presente.”",
      bloques: [
        {
          tag: "LA ANTICIPACIÓN",
          titulo: "Halcón Abrumado",
          quote: "“Tu mente ya vivió escenarios que aún no pasaron.”",
          body:
            "Cuando tu Halcón se acelera, deja de orientar y empieza a fabricar futuros. Anticipas riesgos, conflictos, rupturas… y sin darte cuenta empiezas a habitar emocionalmente algo que todavía no existe.",
        },
        {
          tag: "LA ABSORCIÓN",
          titulo: "Delfín Ansioso",
          quote: "“Sientes el conflicto antes de que exista.”",
          body:
            "Tu sensibilidad deja de filtrar el presente y empieza a cargar emociones del futuro. Sostienes tensiones que todavía no llegaron y conversaciones que nadie tuvo. Lo que era intuición se vuelve ansiedad silenciosa.",
        },
      ],
      resultado: {
        kicker: "EL RESULTADO REAL",
        body:
          "Terminas vacío, no por ver de más sino por vivir lo que aún no llegó. El Halcón no se cansa por anticipar, se agota por habitar emocionalmente cada escenario futuro. Tu fuerza se gasta sosteniendo algo que todavía nadie está viviendo.",
      },
    },

    tension3: {
      kicker: "TU VENTAJA SIN REGULACIÓN",
      frase: "Puede convertir tu visión en agotamiento emocional.",
    },

    regulacion: {
      kicker: "REGULACIÓN",
      titulo: "Sostener el futuro sin perderte en él.",
      rightLabel: "MADUREZ ESTRATÉGICA",
      intro:
        "La evolución del HALCÓN no es ver menos, es aprender a no habitar el futuro.",
      bloques: [
        {
          numero: "01",
          titulo: "VOLVER AL PRESENTE",
          badge: "ANCLAJE",
          body:
            "Lo que todavía no pasó no está pidiendo que lo sostengas. Tu reto es regresar al ahora cada vez que tu mente quiera vivir un futuro que aún no existe.",
        },
        {
          numero: "02",
          titulo: "ANTICIPAR SIN HABITAR",
          badge: "DIRECCIÓN",
          body:
            "Tu Halcón está para ver, no para mudarse al futuro. Apóyate en su visión para decidir hoy, no para cargar emocionalmente lo que aún no llegó.",
        },
        {
          numero: "03",
          titulo: "SENTIR SIN ABSORBER",
          badge: "LÍMITE EMOCIONAL",
          body:
            "Notar el impacto humano no es lo mismo que cargarlo. Aprende a registrar lo que sientes sin convertirlo en tu propia tensión y a sostener sin diluirte.",
        },
      ],
      slider: {
        kicker: "TU ESTADO ACTUAL",
        titulo: "¿Cómo estás jugando hoy?",
        subtitle: "Muévete entre anticipación, balance y absorción.",
        leftLabel: "HALCÓN ABRUMADO",
        centerLabel: "BALANCE",
        rightLabel: "DELFÍN ANSIOSO",
        states: [
          {
            max: 33,
            titulo: "HALCÓN ABRUMADO",
            texto:
              "Tu mente ya vivió escenarios que aún no pasaron.\n\nEmpezaste a desaparecer dentro de ellos.",
          },
          {
            max: 66,
            titulo: "BALANCE",
            texto:
              "Anticipas y sientes sin perderte.\n\nVes el futuro mientras habitas el presente.",
          },
          {
            max: 100,
            titulo: "DELFÍN ANSIOSO",
            texto:
              "Estás cargando tensiones que todavía no existen.\n\nLa anticipación empezó a costarte el presente.",
          },
        ],
      },
    },

    cierre: {
      kicker: "EVOLUCIÓN",
      titulo: "La presión no vino a destruir tu ventaja.",
      highlight: "Vino a enseñarte a usarla mejor.",
      subtitle:
        "No necesitas dejar de cuidar a los demás.\nNecesitas volver a ti.",
    },
  },

  delfin_leon: {
    dominant: "DELFÍN",
    secondary: "LEÓN",

    portada: {
      kicker: "PLAY YOUR GAME™",
      kickerRight: "DELFÍN dominante · LEÓN secundario",
      quote:
        "Tu problema no es falta de empatía.\nEs sostener tanto a los demás…\nque empiezas a desaparecer cuidándolos.",
      footerLeft: "La presión no cambia cómo juegas.\nRevela cómo juegas.",
      footerRight: "DELFÍN + LEÓN",
    },

    tension1: {
      kicker: "LA PRESIÓN REVELA",
      frase:
        "Cuando todo se tensa…\nempiezas a cargar emocionalmente lo que no te toca.",
    },

    arquetipo: {
      kicker: "PARTE I",
      titulo: "TUS DOS FUERZAS",
      rightLabel: "CÓMO REACCIONAS",
      dominante: {
        numero: "01",
        titulo: "EL DELFÍN: Empatía y Cercanía",
        line:
          "Sientes a las personas antes de que digan algo.",
        body:
          "Tu sensibilidad lee el clima del grupo y se involucra de verdad. No solo notas la tensión, te metes a sostener. Por eso las personas confían en ti: transmites cercanía y energía emocional real, y sienten que están siendo vistas cuando estás cerca.",
      },
      secundario: {
        numero: "02",
        titulo: "EL LEÓN: Impulso y Protección",
        line: "Le da fuerza a tu manera de cuidar.",
        body:
          "Tu León protege: cuando alguien necesita apoyo, te mueves. Cuando sientes desconexión, reaccionas con intensidad. No esperas, sostienes, defiendes y empujas. Por eso tu cuidado no es pasivo: tiene fuerza.",
      },
      ventaja: {
        kicker: "TU VENTAJA",
        frase:
          "“Tu ventaja aparece cuando logras convertir sensibilidad en liderazgo emocional humano.”",
        body:
          "Mientras otros gestionan tareas, tú gestionas vínculos con intensidad. Sostienes, proteges y mueves personas desde la cercanía. Eres conexión con fuerza.",
      },
    },

    elcamino: {
      kicker: "EL CAMINO",
      titulo: "Las 4 etapas de evolución.",
      subtitle:
        "PLAY YOUR GAME™ no busca cambiar quién eres.\nBusca ayudarte a usar tu ventaja con intención.",
      etapas: [
        {
          numero: "01 / IDENTIFICACIÓN",
          titulo: "“Así juego naturalmente.”",
          body:
            "Sientes el clima con el Delfín y proteges con el León. Cuidas el vínculo con intensidad real y energía emocional cercana.",
          variant: "neutral",
        },
        {
          numero: "02 / CONCIENCIA",
          titulo: "“Mi fortaleza también puede jugar en mi contra.”",
          body:
            "Asumes que cuidar tanto te aleja de ti. Empiezas a sostener emociones ajenas y a postergar las tuyas sin darte cuenta.",
          variant: "neutral",
        },
        {
          numero: "03 / REGULACIÓN",
          titulo: "“Necesito controlar mi modo sombra.”",
          body:
            "Detectas el momento exacto en el que cargas lo que no te toca o cuando reaccionas con intensidad porque sentiste distancia.",
          variant: "sombra",
        },
        {
          numero: "04 / EVOLUCIÓN",
          titulo: "“Ahora uso mi ventaja con madurez.”",
          body:
            "Aprendes a cuidar sin desaparecer. Sostienes sin perderte y proteges sin explotar.",
          variant: "green",
        },
      ],
    },

    tension2: {
      kicker: "EL COSTO DE LA CERCANÍA",
      frase:
        "Tu manera de cuidar a los demás\nempieza a sentirse como agotamiento emocional.",
    },

    silencio: {
      kicker: "Y AQUÍ EMPIEZA EL PROBLEMA",
      line1: "No soportas la distancia emocional.",
      line2Highlight: "Y empiezas a sostener lo que no te toca.",
    },

    problema: {
      frase:
        "Si nadie está sosteniendo la conexión…\nempiezas a sostenerla tú.",
      footer: "Y poco a poco terminas agotándote intentando que nadie quede afuera.",
    },

    modosombra: {
      kicker: "TENSIÓN PSICOLÓGICA",
      titulo: "EL MODO SOMBRA",
      rightLabel: "CÓMO TE DEGRADAS",
      quote:
        "“Intentas cuidar tanto a todos…\nque empiezas a olvidarte de ti.”",
      bloques: [
        {
          tag: "LA SOBRECARGA",
          titulo: "Delfín Saturado",
          quote: "“Sientes todo… incluso lo que no es tuyo.”",
          body:
            "Tu sensibilidad deja de filtrar. Cargas las emociones del equipo, de tu familia, del entorno. Empiezas a postergarte para sostener a los demás y terminas agotado sin haber dicho que sí.",
        },
        {
          tag: "LA INTENSIDAD",
          titulo: "León Reactivo",
          quote: "“Empezaste a reaccionar emocionalmente cuando sientes distancia.”",
          body:
            "Cuando se acumula demasiada presión emocional, tu León estalla. Lo que era cuidado se convierte en intensidad, defensa o reproche. Las personas que querías acercar empiezan a sentirte como presión.",
        },
      ],
      resultado: {
        kicker: "EL RESULTADO REAL",
        body:
          "Terminas vacío, no por dar de más sino por sostener a todos menos a ti. El Delfín no se cansa por cuidar, se agota por cargar emocionalmente lo que no le toca. Tu fuerza se gasta sosteniendo vínculos que nadie está cuidando contigo.",
      },
    },

    tension3: {
      kicker: "TU VENTAJA SIN REGULACIÓN",
      frase: "Puede convertir tu intensidad en explosión emocional.",
    },

    regulacion: {
      kicker: "REGULACIÓN",
      titulo: "Cuidar sin desaparecer cuidando.",
      rightLabel: "MADUREZ ESTRATÉGICA",
      intro:
        "La evolución del DELFÍN no es sentir menos, es aprender a no sostenerlo todo.",
      bloques: [
        {
          numero: "01",
          titulo: "PONER LÍMITE EMOCIONAL",
          badge: "FRONTERA",
          body:
            "No tienes que cargar todo lo que el otro siente para acompañarlo de verdad. Tu reto es cuidar sin postergarte y sostener sin agotarte.",
        },
        {
          numero: "02",
          titulo: "EXPRESAR ANTES DE EXPLOTAR",
          badge: "VOZ PROPIA",
          body:
            "Lo que no nombras se acumula y termina saliendo como reacción. Aprende a decir lo que sientes cuando todavía es manejable y no cuando ya pesa demasiado.",
        },
        {
          numero: "03",
          titulo: "SOSTENERTE A TI",
          badge: "CENTRO",
          body:
            "Tu León no está solo para defender a los demás, también está para protegerte a ti. Apóyate en su fuerza para cuidar tu propia energía.",
        },
      ],
      slider: {
        kicker: "TU ESTADO ACTUAL",
        titulo: "¿Cómo estás jugando hoy?",
        subtitle: "Muévete entre saturación, balance y reacción.",
        leftLabel: "DELFÍN SATURADO",
        centerLabel: "BALANCE",
        rightLabel: "LEÓN REACTIVO",
        states: [
          {
            max: 33,
            titulo: "DELFÍN SATURADO",
            texto:
              "Estás cargando emociones que no son tuyas.\n\nTe postergaste para sostener a los demás.",
          },
          {
            max: 66,
            titulo: "BALANCE",
            texto:
              "Cuidas con intensidad sin perderte.\n\nSostienes vínculos sin agotarte.",
          },
          {
            max: 100,
            titulo: "LEÓN REACTIVO",
            texto:
              "Reaccionaste emocionalmente para defender el vínculo.\n\nLa intensidad empezó a sentirse como presión.",
          },
        ],
      },
    },

    cierre: {
      kicker: "EVOLUCIÓN",
      titulo: "La presión no vino a destruir tu ventaja.",
      highlight: "Vino a enseñarte a usarla mejor.",
      subtitle:
        "No necesitas sostener emocionalmente a todos.\nNecesitas volver a sostenerte a ti.",
    },
  },

  halcon_leon: {
    dominant: "HALCÓN",
    secondary: "LEÓN",

    portada: {
      kicker: "PLAY YOUR GAME™",
      kickerRight: "HALCÓN dominante · LEÓN secundario",
      quote:
        "Tu problema no es falta de visión.\nEs acelerar tanto el futuro…\nque dejas de escuchar a quienes siguen en el presente.",
      footerLeft: "La presión no cambia cómo juegas.\nRevela cómo juegas.",
      footerRight: "HALCÓN + LEÓN",
    },

    tension1: {
      kicker: "LA PRESIÓN REVELA",
      frase:
        "Cuando ya viste hacia dónde ir…\nempiezas a presionar a quienes no avanzan igual.",
    },

    arquetipo: {
      kicker: "PARTE I",
      titulo: "TUS DOS FUERZAS",
      rightLabel: "CÓMO REACCIONAS",
      dominante: {
        numero: "01",
        titulo: "EL HALCÓN: Visión y Dirección",
        line:
          "Ya viste el siguiente paso antes que el resto lo procese.",
        body:
          "Tu Halcón mira hacia adelante: la oportunidad, el riesgo, la dirección correcta. Mientras otros viven el problema de hoy, tú ya estás trazando el de mañana. No solo entiendes el rumbo, también lo construyes antes de que el resto sepa que existe.",
      },
      secundario: {
        numero: "02",
        titulo: "EL LEÓN: Impulso y Ejecución",
        line: "Le da velocidad a tu visión.",
        body:
          "Tu León no espera. Cuando viste hacia dónde ir, te mueves. Empujas decisiones, aceleras procesos y conviertes la visión en avance real. Por eso tu dirección no se queda en idea: se vuelve movimiento.",
      },
      ventaja: {
        kicker: "TU VENTAJA",
        frase:
          "“Tu ventaja aparece cuando logras convertir visión en dirección con impulso.”",
        body:
          "Mientras otros todavía analizan, tú ya estás moviendo el rumbo. Y mientras avanzas, sigues viendo el siguiente paso. Eres futuro en ejecución.",
      },
    },

    elcamino: {
      kicker: "EL CAMINO",
      titulo: "Las 4 etapas de evolución.",
      subtitle:
        "PLAY YOUR GAME™ no busca cambiar quién eres.\nBusca ayudarte a usar tu ventaja con intención.",
      etapas: [
        {
          numero: "01 / IDENTIFICACIÓN",
          titulo: "“Así juego naturalmente.”",
          body:
            "Lees el futuro con el Halcón y lo mueves con el León. Cuando ves dirección, no esperas: ejecutas y traccionas al resto detrás de ti.",
          variant: "neutral",
        },
        {
          numero: "02 / CONCIENCIA",
          titulo: "“Mi fortaleza también puede jugar en mi contra.”",
          body:
            "Asumes que tu velocidad empieza a sentirse como presión. Lo que para ti es claridad, para otros todavía está siendo procesado.",
          variant: "neutral",
        },
        {
          numero: "03 / REGULACIÓN",
          titulo: "“Necesito controlar mi modo sombra.”",
          body:
            "Detectas el momento exacto en el que empujas la visión sin esperar a nadie o cuando pierdes paciencia con ritmos distintos al tuyo.",
          variant: "sombra",
        },
        {
          numero: "04 / EVOLUCIÓN",
          titulo: "“Ahora uso mi ventaja con madurez.”",
          body:
            "Aprendes a empujar sin atropellar. Mantienes la velocidad sin perder a las personas que vienen contigo.",
          variant: "green",
        },
      ],
    },

    tension2: {
      kicker: "EL COSTO DE LA VELOCIDAD",
      frase:
        "Tu manera de empujar la dirección\nempieza a sentirse como atropello silencioso.",
    },

    silencio: {
      kicker: "Y AQUÍ EMPIEZA EL PROBLEMA",
      line1: "No soportas la lentitud.",
      line2Highlight: "Y empiezas a pasar por encima de quien no avanza igual.",
    },

    problema: {
      frase:
        "Si nadie está moviendo la dirección que ya viste…\nempiezas a empujar tú a todos.",
      footer: "Y poco a poco terminas avanzando sin las personas que querías llevar contigo.",
    },

    modosombra: {
      kicker: "TENSIÓN PSICOLÓGICA",
      titulo: "EL MODO SOMBRA",
      rightLabel: "CÓMO TE DEGRADAS",
      quote:
        "“Tu visión quiere mover todo.\nPero las personas todavía necesitan tiempo para alcanzarte.”",
      bloques: [
        {
          tag: "LA ANTICIPACIÓN",
          titulo: "Halcón Impaciente",
          quote: "“Ya viste el futuro… y no soportas esperar a que el resto llegue.”",
          body:
            "Tu Halcón deja de orientar y empieza a presionar. Aceleras decisiones, empujas dirección y exiges velocidad porque tu mente ya está tres pasos adelante. Lo que era visión se vuelve impaciencia.",
        },
        {
          tag: "EL ATROPELLO",
          titulo: "León Agresivo",
          quote: "“Empujas tan fuerte… que dejas de escuchar.”",
          body:
            "Cuando tu León se acelera, deja de liderar y empieza a atropellar. Conviertes urgencia en presión, intensidad en agresividad. Las personas que querías llevar contigo empiezan a sentirse pasadas por encima.",
        },
      ],
      resultado: {
        kicker: "EL RESULTADO REAL",
        body:
          "Terminas avanzando solo, no por mover demasiado sino por mover sin nadie. El Halcón no se cansa por ver, se agota por empujar tan fuerte que termina alejando a los que quería llevar contigo. Tu fuerza se gasta liderando a un equipo que ya dejó de seguirte.",
      },
    },

    tension3: {
      kicker: "TU VENTAJA SIN REGULACIÓN",
      frase: "Puede convertir tu dirección en presión silenciosa.",
    },

    regulacion: {
      kicker: "REGULACIÓN",
      titulo: "Avanzar sin pasar por encima.",
      rightLabel: "MADUREZ ESTRATÉGICA",
      intro:
        "La evolución del HALCÓN no es bajar la visión, es aprender a traer personas contigo.",
      bloques: [
        {
          numero: "01",
          titulo: "PAUSAR PARA TRAER A OTROS",
          badge: "RITMO COMPARTIDO",
          body:
            "La visión solo lidera si alguien camina contigo. Tu reto es soltar velocidad el tiempo necesario para que el equipo procese lo que tú ya viste.",
        },
        {
          numero: "02",
          titulo: "ESCUCHAR ANTES DE EMPUJAR",
          badge: "TOLERANCIA",
          body:
            "Tu mente entra en modo acelerar demasiado rápido, pero no toda lentitud es resistencia. A veces el otro solo necesita un segundo más para entender lo que tú ya tienes claro.",
        },
        {
          numero: "03",
          titulo: "LIDERAR SIN ATROPELLAR",
          badge: "PRESENCIA",
          body:
            "No necesitas pasar por encima para mover dirección. Tu evolución aparece cuando aprendes a empujar visión sin perder a las personas que querías llevar contigo.",
        },
      ],
      slider: {
        kicker: "TU ESTADO ACTUAL",
        titulo: "¿Cómo estás jugando hoy?",
        subtitle: "Muévete entre impaciencia, balance y atropello.",
        leftLabel: "HALCÓN IMPACIENTE",
        centerLabel: "BALANCE",
        rightLabel: "LEÓN AGRESIVO",
        states: [
          {
            max: 33,
            titulo: "HALCÓN IMPACIENTE",
            texto:
              "Ya viste hacia dónde ir.\n\nY empezaste a presionar a quienes todavía no llegaron.",
          },
          {
            max: 66,
            titulo: "BALANCE",
            texto:
              "Avanzas con visión y traes personas contigo.\n\nEmpujas dirección sin pasar por encima.",
          },
          {
            max: 100,
            titulo: "LEÓN AGRESIVO",
            texto:
              "Tu velocidad se volvió presión.\n\nDejaste de escuchar a quienes querías llevar contigo.",
          },
        ],
      },
    },

    cierre: {
      kicker: "EVOLUCIÓN",
      titulo: "La presión no vino a destruir tu ventaja.",
      highlight: "Vino a enseñarte a usarla mejor.",
      subtitle:
        "No necesitas dejar de empujar visión.\nNecesitas aprender a traer personas contigo.",
    },
  },

  leon_delfin: {
    dominant: "LEÓN",
    secondary: "DELFÍN",

    portada: {
      kicker: "PLAY YOUR GAME™",
      kickerRight: "LEÓN dominante · DELFÍN secundario",
      quote:
        "Tu problema no es falta de fuerza.\nEs querer mover a todos…\nhasta que empiezas a empujarlos emocionalmente.",
      footerLeft: "La presión no cambia cómo juegas.\nRevela cómo juegas.",
      footerRight: "LEÓN + DELFÍN",
    },

    tension1: {
      kicker: "LA PRESIÓN REVELA",
      frase:
        "Cuando sientes desconexión…\nempiezas a empujar emocionalmente para que las cosas avancen.",
    },

    arquetipo: {
      kicker: "PARTE I",
      titulo: "TUS DOS FUERZAS",
      rightLabel: "CÓMO REACCIONAS",
      dominante: {
        numero: "01",
        titulo: "EL LEÓN: Impulso y Presencia",
        line:
          "Tu cuerpo entra en movimiento antes que tu mente.",
        body:
          "Cuando aparece presión, no esperas: mueves, empujas y traccionas. No soportas que las cosas se queden quietas y mucho menos que las personas se enfríen. Tu fuerza no es agresión, es energía que arrastra y hace que el resto vuelva a moverse.",
      },
      secundario: {
        numero: "02",
        titulo: "EL DELFÍN: Cercanía y Conexión",
        line: "Le da humanidad a tu intensidad.",
        body:
          "Tu Delfín no te deja avanzar solo: necesita que el grupo vaya contigo. Lees la energía del equipo, te involucras emocionalmente y mueves desde la cercanía. Por eso tu fuerza no se siente fría: tiene presencia humana.",
      },
      ventaja: {
        kicker: "TU VENTAJA",
        frase:
          "“Tu ventaja aparece cuando logras convertir intensidad en presencia humana real.”",
        body:
          "Mientras otros gestionan tareas, tú mueves personas desde la cercanía. Aceleras sin distancia, lideras con calor humano. Eres impulso con vínculo.",
      },
    },

    elcamino: {
      kicker: "EL CAMINO",
      titulo: "Las 4 etapas de evolución.",
      subtitle:
        "PLAY YOUR GAME™ no busca cambiar quién eres.\nBusca ayudarte a usar tu ventaja con intención.",
      etapas: [
        {
          numero: "01 / IDENTIFICACIÓN",
          titulo: "“Así juego naturalmente.”",
          body:
            "Empujas con el León y conectas con el Delfín. Mueves personas desde la cercanía y haces que el grupo avance contigo.",
          variant: "neutral",
        },
        {
          numero: "02 / CONCIENCIA",
          titulo: "“Mi fortaleza también puede jugar en mi contra.”",
          body:
            "Asumes que tu intensidad empieza a sentirse como presión. Lo que para ti es involucramiento, para otros empieza a sentirse como peso emocional.",
          variant: "neutral",
        },
        {
          numero: "03 / REGULACIÓN",
          titulo: "“Necesito controlar mi modo sombra.”",
          body:
            "Detectas el momento exacto en el que reaccionas porque sentiste distancia, o cuando empujas emocionalmente para que el otro se vuelva a conectar.",
          variant: "sombra",
        },
        {
          numero: "04 / EVOLUCIÓN",
          titulo: "“Ahora uso mi ventaja con madurez.”",
          body:
            "Aprendes a mover sin empujar emocionalmente. Mantienes la cercanía sin convertirla en presión y la energía sin convertirla en intensidad.",
          variant: "green",
        },
      ],
    },

    tension2: {
      kicker: "EL COSTO DE LA INTENSIDAD",
      frase:
        "Tu manera de mover personas\nempieza a sentirse como presión emocional.",
    },

    silencio: {
      kicker: "Y AQUÍ EMPIEZA EL PROBLEMA",
      line1: "No soportas la distancia emocional.",
      line2Highlight: "Y empiezas a empujar para recuperarla.",
    },

    problema: {
      frase:
        "Si nadie está sosteniendo la conexión…\nempiezas a moverla tú con intensidad.",
      footer: "Y poco a poco terminas presionando a las personas que querías acercar.",
    },

    modosombra: {
      kicker: "TENSIÓN PSICOLÓGICA",
      titulo: "EL MODO SOMBRA",
      rightLabel: "CÓMO TE DEGRADAS",
      quote:
        "“Tu intensidad quiere mover a todos…\npero poco a poco empieza a presionarlos.”",
      bloques: [
        {
          tag: "EL IMPULSO",
          titulo: "León Reactivo",
          quote: "“Reaccionas emocionalmente cuando sientes que algo se enfría.”",
          body:
            "Tu cuerpo entra en modo mover antes que tu mente. Empujas conversaciones, aceleras decisiones y elevas el tono porque sentir distancia te consume internamente. Lo que era liderazgo se vuelve presión emocional.",
        },
        {
          tag: "LA NECESIDAD",
          titulo: "Delfín Demandante",
          quote: "“Necesitas sentir cercanía constante para sostener tu energía.”",
          body:
            "Cuando se acumula presión emocional, tu Delfín empieza a pedir conexión. Buscas respuesta, aprobación, presencia. Y sin darte cuenta, terminas cargando el vínculo con expectativas que el otro no puede sostener.",
        },
      ],
      resultado: {
        kicker: "EL RESULTADO REAL",
        body:
          "Terminas agotado, no por mover demasiado sino por mover emocionalmente. El León no se cansa por empujar, se agota por sostener emocionalmente a quienes querías acercar. Tu fuerza se gasta intentando que nadie se enfríe.",
      },
    },

    tension3: {
      kicker: "TU VENTAJA SIN REGULACIÓN",
      frase: "Puede convertir tu cercanía en presión emocional.",
    },

    regulacion: {
      kicker: "REGULACIÓN",
      titulo: "Mover sin empujar emocionalmente.",
      rightLabel: "MADUREZ ESTRATÉGICA",
      intro:
        "La evolución del LEÓN no es bajar la intensidad, es aprender a no convertirla en presión.",
      bloques: [
        {
          numero: "01",
          titulo: "PAUSAR ANTES DE REACCIONAR",
          badge: "CONTROL DE IMPULSO",
          body:
            "No toda distancia es desconexión y no toda lentitud es rechazo. Tu reto es esperar un segundo antes de empujar emocionalmente cuando algo se siente frío.",
        },
        {
          numero: "02",
          titulo: "TOLERAR LA INCOMODIDAD",
          badge: "ESPACIO",
          body:
            "No tienes que mover toda emoción para que el otro se conecte. Aprende a sostener silencio sin convertirlo en presión y a dejar espacio sin sentirte rechazado.",
        },
        {
          numero: "03",
          titulo: "LIDERAR SIN CARGAR TODO",
          badge: "PRESENCIA",
          body:
            "No necesitas mover emocionalmente a todos para que las cosas avancen. Tu evolución aparece cuando aprendes a estar presente sin tener que empujar.",
        },
      ],
      slider: {
        kicker: "TU ESTADO ACTUAL",
        titulo: "¿Cómo estás jugando hoy?",
        subtitle: "Muévete entre reacción, balance y demanda emocional.",
        leftLabel: "LEÓN REACTIVO",
        centerLabel: "BALANCE",
        rightLabel: "DELFÍN DEMANDANTE",
        states: [
          {
            max: 33,
            titulo: "LEÓN REACTIVO",
            texto:
              "Estás empujando emocionalmente cuando sientes distancia.\n\nLa intensidad empezó a sentirse como presión.",
          },
          {
            max: 66,
            titulo: "BALANCE",
            texto:
              "Mueves personas con cercanía sin empujarlas.\n\nLideras con presencia, no con intensidad.",
          },
          {
            max: 100,
            titulo: "DELFÍN DEMANDANTE",
            texto:
              "Necesitas cercanía constante para sostener tu energía.\n\nEl vínculo empezó a pesarle al otro.",
          },
        ],
      },
    },

    cierre: {
      kicker: "EVOLUCIÓN",
      titulo: "La presión no vino a destruir tu ventaja.",
      highlight: "Vino a enseñarte a usarla mejor.",
      subtitle:
        "No necesitas dejar de mover personas.\nNecesitas dejar de empujarlas emocionalmente.",
    },
  },

  lobo_leon: {
    dominant: "LOBO",
    secondary: "LEÓN",

    portada: {
      kicker: "PLAY YOUR GAME™",
      kickerRight: "LOBO dominante · LEÓN secundario",
      quote:
        "Tu problema no es falta de criterio.\nEs controlar tanto lo que pasa…\nque empiezas a reaccionar cuando algo sale del plan.",
      footerLeft: "La presión no cambia cómo juegas.\nRevela cómo juegas.",
      footerRight: "LOBO + LEÓN",
    },

    tension1: {
      kicker: "LA PRESIÓN REVELA",
      frase:
        "Cuando algo se sale del orden…\nempiezas a sentir que tienes que controlarlo tú.",
    },

    arquetipo: {
      kicker: "PARTE I",
      titulo: "TUS DOS FUERZAS",
      rightLabel: "CÓMO REACCIONAS",
      dominante: {
        numero: "01",
        titulo: "EL LOBO: Criterio y Estructura",
        line:
          "Detectas qué está fallando antes de que el resto lo vea.",
        body:
          "Tu Lobo lee el sistema, ordena la información y construye el camino. Te incomoda la improvisación y desconfías del caos. Por eso piensas profundo y exiges estructura: tu fuerza no es velocidad, es claridad bajo presión.",
      },
      secundario: {
        numero: "02",
        titulo: "EL LEÓN: Impulso y Ejecución",
        line: "Le da movimiento a tu análisis.",
        body:
          "Tu León no espera: cuando viste lo que hay que hacer, te mueves. Empujas decisiones, ejecutas con presión y conviertes el criterio en acción real. Por eso tu análisis no se queda quieto: se vuelve avance con control.",
      },
      ventaja: {
        kicker: "TU VENTAJA",
        frase:
          "“Tu ventaja aparece cuando logras convertir criterio en claridad con ejecución.”",
        body:
          "Mientras otros dudan, tú ya entendiste. Y mientras entiendes, ya estás moviendo el primer paso. Eres análisis con tracción.",
      },
    },

    elcamino: {
      kicker: "EL CAMINO",
      titulo: "Las 4 etapas de evolución.",
      subtitle:
        "PLAY YOUR GAME™ no busca cambiar quién eres.\nBusca ayudarte a usar tu ventaja con intención.",
      etapas: [
        {
          numero: "01 / IDENTIFICACIÓN",
          titulo: "“Así juego naturalmente.”",
          body:
            "Lees el sistema con el Lobo y lo mueves con el León. Cuidas estructura y ejecución al mismo tiempo, sin dejar que el caos se instale.",
          variant: "neutral",
        },
        {
          numero: "02 / CONCIENCIA",
          titulo: "“Mi fortaleza también puede jugar en mi contra.”",
          body:
            "Asumes que tu necesidad de orden empieza a pesar. Lo que para ti es criterio, para otros empieza a sentirse como exigencia constante.",
          variant: "neutral",
        },
        {
          numero: "03 / REGULACIÓN",
          titulo: "“Necesito controlar mi modo sombra.”",
          body:
            "Detectas el momento exacto en el que aprietas el control sin necesidad, o cuando reaccionas con intensidad porque algo se salió del plan.",
          variant: "sombra",
        },
        {
          numero: "04 / EVOLUCIÓN",
          titulo: "“Ahora uso mi ventaja con madurez.”",
          body:
            "Aprendes a sostener orden sin reaccionar al primer error. Lideras sin cargar toda la presión y dejas que otros también muevan.",
          variant: "green",
        },
      ],
    },

    tension2: {
      kicker: "EL COSTO DEL CONTROL",
      frase:
        "Tu manera de sostener el orden\nempieza a sentirse como presión constante.",
    },

    silencio: {
      kicker: "Y AQUÍ EMPIEZA EL PROBLEMA",
      line1: "No soportas el caos.",
      line2Highlight: "Y empiezas a cargar toda la presión tú.",
    },

    problema: {
      frase:
        "Si nadie está sosteniendo el orden…\nempiezas a sostenerlo tú a la fuerza.",
      footer: "Y poco a poco terminas exigiendo perfección a quienes vienen contigo.",
    },

    modosombra: {
      kicker: "TENSIÓN PSICOLÓGICA",
      titulo: "EL MODO SOMBRA",
      rightLabel: "CÓMO TE DEGRADAS",
      quote:
        "“Tu necesidad de orden\nempieza a convertirse en presión para los demás.”",
      bloques: [
        {
          tag: "EL HIPERCONTROL",
          titulo: "Lobo Rígido",
          quote: "“Empiezas a controlar cada detalle para que nada salga del plan.”",
          body:
            "Tu Lobo deja de cuidar la decisión y empieza a vigilar todo. Acumulas tensión, exiges perfección y desconfías de los ritmos ajenos. Lo que era criterio se vuelve rigidez bajo presión.",
        },
        {
          tag: "LA REACCIÓN",
          titulo: "León Explosivo",
          quote: "“Cuando algo se sale del plan… reaccionas con intensidad.”",
          body:
            "Cuando la presión se acumula demasiado, tu León estalla. Lo que sostuviste en silencio durante semanas sale de golpe. Las personas empiezan a sentirte como exigencia que llegó tarde a explotar.",
        },
      ],
      resultado: {
        kicker: "EL RESULTADO REAL",
        body:
          "Terminas agotado, no por pensar de más sino por cargar toda la presión tú. El Lobo no se cansa por analizar, se agota por sostener un orden que nadie más está cuidando. Tu fuerza se gasta exigiendo perfección a un sistema que sigue improvisando.",
      },
    },

    tension3: {
      kicker: "TU VENTAJA SIN REGULACIÓN",
      frase: "Puede convertir tu claridad en presión silenciosa.",
    },

    regulacion: {
      kicker: "REGULACIÓN",
      titulo: "Sostener orden sin cargar toda la presión.",
      rightLabel: "MADUREZ ESTRATÉGICA",
      intro:
        "La evolución del LOBO no es soltar el criterio, es aprender a no cargar todo el control.",
      bloques: [
        {
          numero: "01",
          titulo: "TOLERAR LO IMPREVISTO",
          badge: "FLEXIBILIDAD",
          body:
            "No todo desvío es caos y no todo error necesita corrección inmediata. Tu reto es soltar la exigencia de perfección antes de que se vuelva presión interna.",
        },
        {
          numero: "02",
          titulo: "PAUSAR ANTES DE REACCIONAR",
          badge: "CONTROL DE IMPULSO",
          body:
            "Lo que acumulas en silencio termina saliendo como reacción. Aprende a expresar la tensión cuando todavía es manejable y no cuando ya está por explotar.",
        },
        {
          numero: "03",
          titulo: "SOLTAR EL CONTROL",
          badge: "CONFIANZA",
          body:
            "No necesitas sostener cada detalle para que las cosas funcionen. Tu evolución aparece cuando aprendes a dejar que otros también muevan, aunque no lo hagan exactamente como tú.",
        },
      ],
      slider: {
        kicker: "TU ESTADO ACTUAL",
        titulo: "¿Cómo estás jugando hoy?",
        subtitle: "Muévete entre rigidez, balance y reacción.",
        leftLabel: "LOBO RÍGIDO",
        centerLabel: "BALANCE",
        rightLabel: "LEÓN EXPLOSIVO",
        states: [
          {
            max: 33,
            titulo: "LOBO RÍGIDO",
            texto:
              "Estás controlando cada detalle para que nada falle.\n\nLa exigencia empezó a pesarte más que el orden.",
          },
          {
            max: 66,
            titulo: "BALANCE",
            texto:
              "Sostienes orden sin cargar toda la presión.\n\nDecides con criterio y dejas espacio para que otros muevan.",
          },
          {
            max: 100,
            titulo: "LEÓN EXPLOSIVO",
            texto:
              "Acumulaste demasiada tensión bajo control.\n\nLo que callaste salió de golpe.",
          },
        ],
      },
    },

    cierre: {
      kicker: "EVOLUCIÓN",
      titulo: "La presión no vino a destruir tu ventaja.",
      highlight: "Vino a enseñarte a usarla mejor.",
      subtitle:
        "No necesitas controlar todo para avanzar.\nNecesitas aprender a soltar.",
    },
  },

  halcon_lobo: {
    dominant: "HALCÓN",
    secondary: "LOBO",

    portada: {
      kicker: "MAPA DE EVOLUCIÓN",
      kickerRight: "FILOSOFÍA REAL",
      quote:
        "“Tu mente ya está en el futuro…\nmientras las personas todavía intentan alcanzarte.”",
      footerLeft: "LA PRESIÓN NO CAMBIA CÓMO JUEGAS. REVELA CÓMO JUEGAS.",
      footerRight: "VER MI ESPEJO",
    },

    tension1: {
      kicker: "EL ESPEJO HUMANO",
      frase:
        "“Tu problema no es falta de visión.\nEs desconectarte del presente mientras persigues dirección.”",
    },

    arquetipo: {
      kicker: "PARTE I",
      titulo: "TUS DOS FUERZAS",
      rightLabel: "CÓMO REACCIONAS",
      dominante: {
        numero: "01",
        titulo: "EL HALCÓN: Visión e Impaciencia",
        line:
          "Mientras otros viven el problema de hoy… tu cabeza ya está intentando resolver mañana.",
        body:
          "Tu mente ya está pensando en lo que viene después. Te desespera sentir que nadie está viendo el panorama completo. Cuando no ves dirección, pierdes paciencia de inmediato. El presente empieza a sentirse demasiado lento para tu mente.",
      },
      secundario: {
        numero: "02",
        titulo: "EL LOBO: Estructura y Rigor",
        line: "El caos te drena. Necesitas sentir que esto tiene sentido.",
        body:
          "No confías en los golpes de suerte. Buscas orden, reglas claras y un paso a paso organizado. El Lobo te da estabilidad para que el Halcón no destruya el camino por pura impaciencia.",
      },
      ventaja: {
        kicker: "TU VENTAJA",
        frase: "“Tu ventaja aparece cuando logras convertir visión en dirección.”",
        body:
          "Eres capaz de adelantarte, ver la ruta antes que nadie y organizar los pasos hoy para avanzar sobre terreno seguro. Eres dirección en medio de la duda.",
      },
    },

    elcamino: {
      kicker: "EL CAMINO",
      titulo: "Las 4 Etapas de Evolución",
      subtitle:
        "Tu mapa humano para salir del piloto automático y aprender a regularte.",
      etapas: [
        {
          numero: "01 / IDENTIFICACIÓN",
          titulo: "“Así juego naturalmente.”",
          body:
            "Adelantas la mirada con el Halcón y pones reglas con el Lobo. Es tu zona cómoda de mayor destreza táctica.",
          variant: "neutral",
        },
        {
          numero: "02 / CONCIENCIA",
          titulo: "“Mi fortaleza puede jugar en mi contra.”",
          body:
            "Asumes que tu velocidad mental puede aislarte. Ves que tu impaciencia por llegar al futuro abruma al resto.",
          variant: "neutral",
        },
        {
          numero: "03 / REGULACIÓN",
          titulo: "“Necesito controlar mi modo sombra.”",
          body:
            "Detectas el momento exacto en el que te desconectas emocionalmente o cuando intentas controlarlo todo compulsivamente.",
          variant: "sombra",
        },
        {
          numero: "04 / EVOLUCIÓN",
          titulo: "“Ahora uso mi ventaja con madurez.”",
          body:
            "Consigues ver el futuro sin desaparecer del presente. Creas dirección manteniendo una presencia humana hoy.",
          variant: "green",
        },
      ],
    },

    tension2: {
      kicker: "FILOSOFÍA DE PRESIÓN",
      frase: "“La presión no cambia cómo juegas.\nRevela cómo juegas.”",
    },

    silencio: {
      kicker: "ALERTA",
      line1: "Tu ventaja…",
      line2Highlight: "empezó a jugar en tu contra.",
    },

    problema: {
      frase: "Y aquí empieza el desgaste.",
      footer: "SISTEMA BAJO PRESIÓN",
    },

    modosombra: {
      kicker: "TENSIÓN PSICOLÓGICA",
      titulo: "EL MODO SOMBRA",
      rightLabel: "CÓMO TE DEGRADAS",
      quote:
        "“Tu mente vive adelante.\nPero las personas todavía necesitan sentirte aquí.”",
      bloques: [
        {
          tag: "LA DESCONEXIÓN",
          titulo: "Halcón Desconectado",
          quote: "“Estás físicamente ahí… pero emocionalmente desapareces.”",
          body:
            "Las personas empiezan a sentir que ya no pueden alcanzarte. Tu cabeza sigue adelante, pero tú dejas de estar presente. Exiges metas impecables y te irritas ante el desorden de hoy.",
        },
        {
          tag: "EL CONTROL",
          titulo: "Lobo Saturado",
          quote: "“Empiezas a cargar todo solo… porque sientes que nadie más lo hará bien.”",
          body:
            "Te perdiste intentando controlar cada pequeño detalle. Desconfías de los ritmos ajenos, te abrumas de tareas operativas y asfixias la iniciativa de tu entorno.",
        },
      ],
      resultado: {
        kicker: "EL RESULTADO REAL",
        body:
          "Vivir en el mañana te cuesta tu presencia de hoy. Te alejas de las personas que te necesitan en este momento y construyes un cansancio profundo intentando forzar la realidad a encajar en tu molde mental.",
      },
    },

    tension3: {
      kicker: "EL REGRESO",
      frase:
        "“La madurez no es cambiar quién eres.\nEs aprender a jugar con intención.”",
    },

    regulacion: {
      kicker: "PARTE III",
      titulo: "VOLVER AL PRESENTE",
      rightLabel: "PRÁCTICA DIARIA",
      intro:
        "Para no desconectarte ni saturarte cuando la tensión sube, necesitas anclar tu cabeza al aquí y al ahora con gestos sencillos.",
      bloques: [
        {
          numero: "01",
          titulo: "Elegir Rumbo (Mañana)",
          badge: "Un solo paso hoy",
          body:
            "Tómate 10 minutos al empezar. Define una sola meta concreta para darle rumbo a tu mente. Olvídate de la lista infinita; concéntrate en pisar firme hoy.",
        },
        {
          numero: "02",
          titulo: "Presencia Real (Durante el Día)",
          badge: "Quédate aquí",
          body:
            "Cuando hables con alguien, quédate ahí por completo. Tu mente suele estar tres pasos adelante, pensando en lo que viene. Detente, haz una pausa y escucha. Tu equipo necesita encontrarte hoy.",
        },
        {
          numero: "03",
          titulo: "Vaciado Mental (Noche)",
          badge: "Apagar la alerta",
          body:
            "Antes de dormir, escribe en un papel todas las tareas pendientes. Esto le permite a tu Lobo apagar el sentido de control y descansar de verdad, liberando espacio para desconectar.",
        },
      ],
      slider: {
        kicker: "OBSERVACIÓN INTERNA",
        titulo: "¿Cómo estás jugando hoy?",
        subtitle:
          "Desplaza el indicador para ver dónde está tu mente en este momento de tensión.",
        leftLabel: "0% LOBO SATURADO",
        centerLabel: "50% BALANCE",
        rightLabel: "100% HALCÓN DESCONECTADO",
        states: [
          {
            max: 35,
            titulo: "TU TENSIÓN HOY: LOBO SATURADO",
            texto: "Te perdiste intentando controlar todo.",
          },
          {
            max: 65,
            titulo: "TU TENSIÓN HOY: BALANCE",
            texto: "Estás viendo el futuro… sin desaparecer del presente.",
          },
          {
            max: 100,
            titulo: "TU TENSIÓN HOY: HALCÓN DESCONECTADO",
            texto:
              "Tu mente ya está adelante… pero la gente todavía necesita sentirte presente.",
          },
        ],
      },
    },

    cierre: {
      kicker: "TU RETORNO",
      titulo: "La presión no vino a destruir tu ventaja.",
      highlight: "Vino a enseñarte a usarla mejor.",
      subtitle: "No necesitas dejar de ser HALCÓN.\nNecesitas aprender a volver.",
    },
  },
};

export function getMapa(dominant, secondary) {
  if (!dominant || !secondary) return null;
  const key = `${String(dominant).toLowerCase()}_${String(secondary).toLowerCase()}`;
  return MAPAS[key] || null;
}
